import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Droplets, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1672920800748-a5fb6dfd0c2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzbmVha2VyJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg2ODM0NjYwfDA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid min-h-screen bg-obsidian lg:grid-cols-2" data-testid="auth-page">
      <div className="flex flex-col justify-center px-6 py-16 sm:px-16 lg:px-24">
        <Link to="/" data-testid="auth-logo" className="mb-16 flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center border border-aqua/50 text-aqua">
            <Droplets size={18} />
          </span>
          <span className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
            Clean <span className="text-aqua">&</span> Revive
          </span>
        </Link>

        <p className="font-mono text-xs uppercase tracking-[0.25em] text-aqua">
          {mode === "login" ? "Welcome back" : "Join the studio"}
        </p>
        <h1 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter text-white sm:text-5xl">
          {mode === "login" ? "Sign in" : "Create account"}
        </h1>

        <div className="mt-8 flex gap-2">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`auth-mode-${m}`}
              onClick={() => {
                setMode(m);
                setError("");
              }}
              className={`border px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                mode === m ? "border-aqua text-aqua" : "border-white/15 text-zinc-400 hover:border-white/40"
              }`}
            >
              {m === "login" ? "Sign in" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-10 max-w-sm space-y-6">
          {mode === "register" && (
            <div className="space-y-2">
              <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                Full name
              </Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="border-white/15 bg-carbon text-white focus-visible:ring-aqua"
                placeholder="Alex Carter"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="border-white/15 bg-carbon text-white focus-visible:ring-aqua"
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="border-white/15 bg-carbon text-white focus-visible:ring-aqua"
              placeholder="Minimum 6 characters"
            />
          </div>

          {error && (
            <p data-testid="auth-error" className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 border border-aqua bg-aqua px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua disabled:opacity-50"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
      </div>

      <div className="relative hidden lg:block">
        <img src={SIDE_IMG} alt="Freshly cleaned sneakers" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/30 to-transparent" />
        <div className="absolute bottom-16 left-16 max-w-sm">
          <p className="font-display text-3xl font-bold uppercase leading-tight tracking-tight text-white">
            Track every pair, door to door.
          </p>
          <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-aqua">
            Collection — Cleaning — Delivery
          </p>
        </div>
      </div>
    </main>
  );
}
